import { AnalyticsPixelIds } from 'core/analytics/entities';
import {
  AmplitudeServerService,
  AnalyticsServerService,
  FunnelStepServerService,
  AppnebulaHttpAxonService,
  AppnebulaHttpFacebookService,
  AppnebulaHttpSnapchatService,
  AppnebulaHttpTaboolaService,
  AppnebulaHttpTikTokService,
  ArrivalServerService,
  AxonServiceBuilder,
  FacebookServiceBuilder,
  HttpAxonService,
  HttpFacebookService,
  HttpGTMService,
  HttpSnapchatService,
  HttpSnitcherService,
  HttpTaboolaService,
  HttpTikTokService,
  SnapchatServiceBuilder,
  SnitcherServiceBuilder,
  TaboolaServiceBuilder,
  TikTokServiceBuilder,
} from 'core/analytics/services';
import { AuthService } from 'core/auth/services';
import { ServerObservabilitySystem } from 'core/common/observability/services/ServerObservabilitySystem';
import { CookiesServerService } from 'core/common/services/cookie';
import { ContentService } from 'core/content/services';
import { OneTrustCookieConsentService } from 'core/cookie-consent/services';
import { featureFlagsConfig } from 'core/feature-flags/config';
import { FeatureFlagsConfig } from 'core/feature-flags/entities';
import {
  CookiesIdFeatureFlagsDecorator,
  GrowthBookFeatureFlags,
  QuerySettingFeatureFlagsDecorator,
} from 'core/feature-flags/services';
import { FunnelServiceBuilder } from 'core/funnel/builders';
import { ServerSideProps } from 'core/funnel/entities';
import { HandlebarsTemplateEngine } from 'core/funnel/services';
import { HumanDesignReportService } from 'core/human-design/services';
import { LocalizationService } from 'core/localization/services';
import { NatalChartService } from 'core/natal-chart/services';
import { OffersService } from 'core/offers/services';
import { PaymentService } from 'core/payments/services';
import { FaceDetectionService, PalmDetectionService } from 'core/scan/services';
import { SupportFormService } from 'core/support/services';
import {
  AutocompleteService,
  LocationServerService,
  SubscriptionsService,
  UserReadingService,
  UserService,
  UtmTagsServerService,
} from 'core/user/services';
import { ZodiacService } from 'core/zodiac/services';
import {
  AppServerEnvironment,
  ChannelAttributionServerService,
  ClarityHeatmapRecordingServerService,
  ContentManagerServerService,
  EmailManagerService,
  HttpClientBuilder,
  LoggerBuilder,
  MetaServerService,
} from '../services';
import { ErrorMonitoringServerService } from '../services/errorMonitoring';
import { FunnelSettingsService } from '../services/funnel-settings';
import { StorePersistenceServerService } from '../store/services';
import { Config } from './ConfigContext';
import type { Services } from './ServicesContext';

export const initServerDependencies = (
  config: Config,
  options: {
    analyticsPixelIds?: AnalyticsPixelIds;
    serverContext?: ServerSideProps;
    features?: FeatureFlagsConfig['features'];
  },
): Services => {
  const { serverContext, features } = options;
  const queryParams = serverContext ? serverContext.query : {};

  const cookiesService = new CookiesServerService(
    {
      domain: config.rootDomain,
    },
    serverContext,
  );

  const loggingService = new LoggerBuilder().getResult();

  const observabilitySystem = new ServerObservabilitySystem({
    defaultTracerName: config.observability.defaultTracerName,
  });

  const httpClient = new HttpClientBuilder(config.apiUrl).addRequestId().getResult();
  const publicHttpClient = new HttpClientBuilder(config.apiUrl, {
    withCredentials: false,
    headers: {},
  }).getResult();

  const errorMonitoringService = new ErrorMonitoringServerService();
  const metaService = new MetaServerService();

  const utmTagsService = UtmTagsServerService.createFromQueryParams(
    cookiesService,
    loggingService,
    queryParams,
  );
  const amplitudeService = new AmplitudeServerService(
    config.analytics.amplitudeApiKey,
    cookiesService,
  );
  const analyticsService = new AnalyticsServerService(amplitudeService);
  const appEnvironment = new AppServerEnvironment();

  const growthBookInitConfig = {
    ...featureFlagsConfig,
    features: { ...(featureFlagsConfig.features ?? {}), ...(features ?? {}) },
  };

  const growthBookFeatureFlags = new GrowthBookFeatureFlags(
    growthBookInitConfig,
    appEnvironment,
    config,
    loggingService,
  );
  const featureFlagsWithIdentify = new CookiesIdFeatureFlagsDecorator(
    growthBookFeatureFlags,
    cookiesService,
    config,
    loggingService,
  );
  const featureFlags = new QuerySettingFeatureFlagsDecorator(featureFlagsWithIdentify);
  const cookieConsentService = new OneTrustCookieConsentService(cookiesService, loggingService);
  const funnelSettingsService = new FunnelSettingsService(config);
  const funnelStepService = new FunnelStepServerService();

  const coreFacebookService = new FacebookServiceBuilder(
    new HttpFacebookService(cookieConsentService),
  ).getResult();
  const facebookService = new AppnebulaHttpFacebookService(coreFacebookService);
  const coreTikTokService = new TikTokServiceBuilder(
    new HttpTikTokService(cookiesService, cookieConsentService),
  ).getResult();
  const tikTokService = new AppnebulaHttpTikTokService(coreTikTokService);
  const coreTaboolaService = new TaboolaServiceBuilder(
    new HttpTaboolaService(cookieConsentService),
  ).getResult();
  const taboolaService = new AppnebulaHttpTaboolaService(coreTaboolaService);
  const coreAxonService = new AxonServiceBuilder(
    new HttpAxonService(cookieConsentService),
  ).getResult();
  const axonService = new AppnebulaHttpAxonService(coreAxonService);
  const coreSnapchatService = new SnapchatServiceBuilder(
    new HttpSnapchatService(cookieConsentService),
  ).getResult();
  const snapchatService = new AppnebulaHttpSnapchatService(coreSnapchatService);
  const gtmService = new HttpGTMService(observabilitySystem, funnelSettingsService);
  const zodiacService = new ZodiacService();
  const locationService = new LocationServerService(httpClient);
  const userService = new UserService(httpClient);
  const userReadingService = new UserReadingService(httpClient);
  const natalChartService = new NatalChartService(httpClient);
  const contentService = new ContentService(httpClient);
  const authService = new AuthService(httpClient);
  const emailManagerService = new EmailManagerService(httpClient);
  const autocompleteService = new AutocompleteService(config.geocodeUrl, config.geocodeApiKey);
  const offersService = new OffersService(httpClient, locationService, funnelSettingsService);
  const palmDetectionService = new PalmDetectionService(httpClient);
  const faceDetectionService = new FaceDetectionService(httpClient);
  const templateEngine = new HandlebarsTemplateEngine();
  const funnelService = FunnelServiceBuilder.create(config);
  const humanDesignReportService = new HumanDesignReportService(httpClient);
  const heatmapRecordingService = new ClarityHeatmapRecordingServerService();
  const storePersistenceService = new StorePersistenceServerService();
  const channelAttributionService = new ChannelAttributionServerService();
  const localizationService = new LocalizationService();
  const subscriptionService = new SubscriptionsService(httpClient);
  const contentManagerService = new ContentManagerServerService(publicHttpClient);
  const paymentService = new PaymentService(
    httpClient,
    loggingService,
    funnelSettingsService,
    locationService,
    featureFlags,
    config,
  );
  const supportFormService = new SupportFormService(httpClient);
  const snitcherService = new SnitcherServiceBuilder(
    new HttpSnitcherService(httpClient, loggingService, featureFlags),
  )
    .addFunnelContext(funnelSettingsService, funnelStepService)
    .getResult();
  const arrivalService = new ArrivalServerService();

  return {
    featureFlags,
    appEnvironment,
    loggingService,
    utmTagsService,
    zodiacService,
    cookiesService,
    analyticsService,
    locationService,
    authService,
    userService,
    userReadingService,
    natalChartService,
    contentService,
    autocompleteService,
    palmDetectionService,
    faceDetectionService,
    offersService,
    templateEngine,
    cookieConsentService,
    funnelService,
    humanDesignReportService,
    observabilitySystem,
    heatmapRecordingService,
    storePersistenceService,
    errorMonitoringService,
    metaService,
    channelAttributionService,
    tikTokService,
    taboolaService,
    axonService,
    snapchatService,
    gtmService,
    funnelSettingsService,
    funnelStepService,
    facebookService,
    localizationService,
    httpClient,
    snitcherService,
    arrivalService,
    emailManagerService,
    subscriptionService,
    contentManagerService,
    paymentService,
    supportFormService,
  };
};
